import { ReactFlowProvider, ReactFlow, Node, Edge } from "@xyflow/react";
import { useAtomValue } from "jotai";
import {
  nodeAtomFamily,
  ZapNodeData,
  zapStepNodesAtomFamily,
  allZapStepEdgesAtomFamily,
  suggestedNodeAtomFamily,
  suggestedZapStepsAndEdgesAtomFamily,
} from "@zapier/canvas-state";

import { PreviewProvider } from "../../../context/PreviewProvider";
import { ZapNode } from "../nodeTypes/ZapNode";
import { ZapStepNode } from "../nodeTypes/ZapStepNode";
import { ZapStepEdge } from "../edgeTypes/ZapStepEdge";

export function ZapPreview({ nodeId }: { nodeId: string }) {
  const node = useAtomValue(nodeAtomFamily(nodeId)) as Node<ZapNodeData>;
  const suggestedNode = useAtomValue(suggestedNodeAtomFamily(nodeId));
  const nodeOrSuggestedNode = node ?? suggestedNode;
  const zapSteps = useAtomValue(zapStepNodesAtomFamily(nodeId));
  const zapStepEdges = useAtomValue(allZapStepEdgesAtomFamily(nodeId));

  const suggestedZapStepsAndEdges = useAtomValue(
    suggestedZapStepsAndEdgesAtomFamily(nodeId),
  );

  if (nodeOrSuggestedNode.type !== "zap") {
    // TODO: implement preview for other types
    return null;
  }

  let nodes: Node[] = [];
  let edges: Edge[] = [];

  if (node) {
    // Transform nodes with new IDs to avoid shared state
    // Bugs you would see:
    // Selected state is shared with the real node: https://cdn.zappy.app/4f85a0440f688c8df276a3bc0e56ece8.gif
    // Expanding / collapsing the real node affects the copy: https://cdn.zappy.app/40e74ed41a78c3b9c9870772554400a6.gif
    nodes = [
      {
        ...node,
        id: `_COPY_${node.id}`,
      },
      ...zapSteps.map((zStepNode) => ({
        ...zStepNode,
        id: `_COPY_${zStepNode.id}`,
        hidden: false,
        parentId: `_COPY_${zStepNode.parentId}`,
      })),
    ];

    // Transform edges with new IDs to avoid shared state
    edges = zapStepEdges.map((edge) => ({
      ...edge,
      id: `_COPY_${edge.id}`,
      source: `_COPY_${edge.source}`,
      target: `_COPY_${edge.target}`,
    }));
  }

  if (!node && suggestedZapStepsAndEdges) {
    nodes = suggestedZapStepsAndEdges.nodes;
    edges = suggestedZapStepsAndEdges.edges;
  }

  return (
    <ReactFlowProvider>
      <PreviewProvider value={true}>
        <ReactFlow
          panOnDrag={false}
          panOnScroll={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          nodes={nodes}
          edges={edges}
          fitView
          nodeTypes={{
            zap: ZapNode,
            zapStep: ZapStepNode,
          }}
          edgeTypes={{
            zapStepEdge: ZapStepEdge,
          }}
          proOptions={{ hideAttribution: true }}
        />
      </PreviewProvider>
    </ReactFlowProvider>
  );
}
